import React, { useState, useEffect, useRef } from 'react';
import { chatService, usersService } from '../../services/firestoreService';
import './ClassChat.css';

const ClassChat = ({ sessionId, studentId, userName }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('chat'); // 'chat' or 'question'
  const [studentsMap, setStudentsMap] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Load students map
    const loadStudents = async () => {
      const students = await usersService.getAllStudents();
      const map = {};
      students.forEach(s => {
        map[s.id] = s;
      });
      setStudentsMap(map);
    };
    loadStudents();

    // Subscribe to messages
    const unsubscribe = chatService.subscribeToMessages(sessionId, (messagesList) => {
      setMessages(messagesList.reverse()); // Reverse to show oldest first
    });

    return () => unsubscribe();
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await chatService.sendMessage(sessionId, studentId, newMessage.trim(), messageType);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  return (
    <div className="class-chat-container">
      <div className="chat-header">
        <h2>💬 Class Chat & Q&A</h2>
        <div className="message-type-toggle">
          <button
            className={`toggle-btn ${messageType === 'chat' ? 'active' : ''}`}
            onClick={() => setMessageType('chat')}
          >
            💬 Chat
          </button>
          <button
            className={`toggle-btn ${messageType === 'question' ? 'active' : ''}`}
            onClick={() => setMessageType('question')}
          >
            ❓ Ask Question
          </button>
        </div>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const student = studentsMap[message.studentId];
            const isOwnMessage = message.studentId === studentId;
            const isQuestion = message.messageType === 'question';

            return (
              <div
                key={message.id}
                className={`message-item ${isOwnMessage ? 'own-message' : ''} ${isQuestion ? 'question' : ''}`}
              >
                <div className="message-header">
                  <span className="sender-name">
                    {isOwnMessage ? 'You' : (student?.name || 'Unknown')}
                  </span>
                  {isQuestion && <span className="question-badge">❓ Question</span>}
                  <span className="message-time">
                    {new Date(message.createdAt).toLocaleTimeString()}
                  </span>
                </div>
                <div className="message-content">{message.message}</div>
                {message.answer && (
                  <div className="answer-content">
                    <strong>Answer:</strong> {message.answer}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={messageType === 'question' ? 'Ask a question...' : 'Type a message...'}
          className="message-input"
        />
        <button type="submit" className="send-btn">
          {messageType === 'question' ? '❓ Ask' : '💬 Send'}
        </button>
      </form>
    </div>
  );
};

export default ClassChat;

